import { useEffect, useMemo } from "react";
import { Select, Typography, Col, Row } from "antd";
import dayjs from "dayjs";
import { EventInfoTextConstants } from "./constants";
import { useEventContext } from "../../../context/EventContext/EventContext";
import { useEventTagContext } from "../../../context/EventTagContext/EventTagContext";
import { cultures, VolunteersEventType, volunteersEventTypeLabels, EventTextRules } from "../../../context/EventContext/constants";
import SwitchOptionComponent from "../../../components/SwitchComponent";
import SelectComponent from "../../../components/SelectComponent";
import InputComponent from "../../../components/InputComponent";
import InputNumberComponent from "../../../components/InputNumberComponent";
import DatePickerComponent from "../../../components/DatePickerComponent";

const eventTypeOptions = Object.values(VolunteersEventType)
	.filter((value): value is VolunteersEventType => typeof value === "number")
	.map(value => ({ label: volunteersEventTypeLabels[value], value: String(value) }));

const EventInfo = () => {
	const { eventForm, setEventForm } = useEventContext();
	return (
		<>
			<Row gutter={[20, 16]}>
				<Col xs={24} sm={12} md={6} lg={4}>
					<InputNumberComponent
						title={EventInfoTextConstants.displayOrder.label}
						positiveOnly
						value={eventForm.eventPreview.displayOrder}
						placeholder={EventInfoTextConstants.displayOrder.placeholder}
						onChange={value =>
							setEventForm(prev => ({
								...prev,
								eventPreview: {
									...prev.eventPreview,
									displayOrder: value === null || value === "" ? undefined : Number(value),
								},
							}))
						}
					/>
				</Col>
				<Col xs={24} sm={12} md={10} lg={8}>
					<SelectComponent
						title={EventInfoTextConstants.eventType.label}
						isRequired
						options={eventTypeOptions}
						value={eventForm.eventType !== undefined ? String(eventForm.eventType) : null}
						onChange={value => setEventForm(prev => ({ ...prev, eventType: Number(value) as VolunteersEventType }))}
					/>
				</Col>
			</Row>
			<Row gutter={[20, 16]}>
				<Col xs={24} sm={16} md={12} lg={8}>
					<SwitchOptionComponent
						title={EventInfoTextConstants.isFeatured.label}
						helperText={EventInfoTextConstants.isFeatured.helperText}
						value={eventForm.eventPreview.isFeatured}
						onChange={checked =>
							setEventForm(prev => ({ ...prev, eventPreview: { ...prev.eventPreview, isFeatured: checked } }))
						}
					/>	
				</Col>
			</Row>
		</>
	);
};

export const EventTags = () => {
	const { eventForm, setEventForm } = useEventContext();
	const { eventTagList, getEventTagList } = useEventTagContext();

	useEffect(() => {
		getEventTagList();
	}, []);

	const tagOptions = useMemo(
		() => eventTagList.map(tag => ({ label: tag.tagName, value: tag.eventTagId })),
		[eventTagList]
	);

	return (
		<>
			<Row gutter={[20, 16]}>
				<Col xs={24} sm={24} md={12} lg={12}>
					<InputComponent
						title={EventInfoTextConstants.eventTags.locationLabel}
						placeholder={EventInfoTextConstants.eventTags.locationPlaceholder}
						value={eventForm.locationText}
						maxLength={EventTextRules.locationTextMaxLength}
						onChange={e => setEventForm(prev => ({ ...prev, locationText: e.target.value }))}
					/>
				</Col>
				<Col xs={24} sm={24} md={12} lg={12}>
					<InputComponent
						title={EventInfoTextConstants.eventTags.projectDateTextLabel}
						placeholder={EventInfoTextConstants.eventTags.projectDateTextPlaceholder}
						value={eventForm.projectDateText}
						maxLength={EventTextRules.projectDateTextMaxLength}
						onChange={e => setEventForm(prev => ({ ...prev, projectDateText: e.target.value }))}
					/>
				</Col>
				<Col xs={24} sm={24} md={12} lg={10}>
					<Typography.Text>{EventInfoTextConstants.eventTags.tagsLabel}</Typography.Text>
					<Select
						mode="multiple"
						size="large"
						style={{ width: "100%" }}
						placeholder={EventInfoTextConstants.eventTags.tagsPlaceholder}
						maxCount={EventInfoTextConstants.eventTags.maxTagCount}
						options={tagOptions}
						value={eventForm.eventTagIds}
						onChange={(values: string[]) => setEventForm(prev => ({ ...prev, eventTagIds: values }))}
						optionFilterProp="label"
					/>
				</Col>
			</Row>
		</>
	);
};

export const AppliableEventSettings = () => {
	const { eventForm, setEventForm } = useEventContext();

	return (
		<>
			<Row gutter={[20, 16]}>
				<Col xs={24} sm={24} md={12} lg={12}>
					<DatePickerComponent
						title={EventInfoTextConstants.appliableSettings.projectDateLabel}
						allowClear
						disablePastDates
						value={eventForm.projectDate ? dayjs(eventForm.projectDate) : null}
						onChange={date => setEventForm(prev => ({ ...prev, projectDate: date || null }))}
					/>
				</Col>
				<Col xs={24} sm={24} md={12} lg={12}>
					<SwitchOptionComponent
						title={EventInfoTextConstants.appliableSettings.applicationStatusLabel}
						helperText={EventInfoTextConstants.appliableSettings.applicationStatusHelperText}
						value={eventForm.isAppliable}
						onChange={checked => setEventForm(prev => ({ ...prev, isAppliable: checked }))}
					/>
				</Col>
			</Row>
		</>
	);
};

export const NotAppliableEventSettings = () => {
	const { eventForm, setEventForm } = useEventContext();
	const trCulture = cultures[0];
	const enCulture = cultures[1];
	const trCategoryTag = eventForm.eventPreview.eventPreviewLocalizable.find(x => x.culture === trCulture)?.categoryTag ?? "";
	const enCategoryTag = eventForm.eventPreview.eventPreviewLocalizable.find(x => x.culture === enCulture)?.categoryTag ?? "";

	const onCategoryTagChange = (culture: string, value: string) => {
		setEventForm(prev => ({
			...prev,
			categoryTag: culture === trCulture ? value : prev.categoryTag,
			eventPreview: {
				...prev.eventPreview,
				eventPreviewLocalizable: prev.eventPreview.eventPreviewLocalizable.map(item =>
					item.culture === culture ? { ...item, categoryTag: value } : item
				),
			},
		}));
	};

	return (
		<>
			<Row gutter={[20, 16]}>
				<Col xs={24} sm={24} md={8} lg={8}>
					<InputComponent
						title={EventInfoTextConstants.notAppliableSettings.categoryTagTrLabel}
						placeholder={EventInfoTextConstants.notAppliableSettings.categoryTagTrPlaceholder}
						isRequired
						value={trCategoryTag}
						maxLength={EventTextRules.categoryTagMaxLength}
						onChange={e => onCategoryTagChange(trCulture, e.target.value)}
					/>
				</Col>
				<Col xs={24} sm={24} md={8} lg={8}>
					<InputComponent
						title={EventInfoTextConstants.notAppliableSettings.categoryTagEnLabel}
						placeholder={EventInfoTextConstants.notAppliableSettings.categoryTagEnPlaceholder}
						isRequired
						value={enCategoryTag}
						maxLength={EventTextRules.categoryTagMaxLength}
						onChange={e => onCategoryTagChange(enCulture, e.target.value)}
					/>
				</Col>
				<Col xs={24} sm={24} md={8} lg={8}>
					<SwitchOptionComponent
						title={EventInfoTextConstants.notAppliableSettings.customButtonLabel}
						helperText={EventInfoTextConstants.notAppliableSettings.customButtonHelperText}
						value={eventForm.eventDetail.hasCustomButton}
						onChange={checked =>
							setEventForm(prev => ({ ...prev, eventDetail: { ...prev.eventDetail, hasCustomButton: checked } }))
						}
					/>
				</Col>
			</Row>
		</>
	);
};

export default EventInfo;
